<script lang="ts" setup>
import { token } from "@/init";
import { HTTP } from "@/util";
import { PropType, ref } from "vue";
import { useStore } from "vuex";
import { PMDoc } from "./PromomatsDocument";
import { ProgressSpinner } from "@/components";
import { Modal } from "@/components";
import { WorkspaceDocumentFragment } from "@/graphql/operations";
import Document from "./Document.vue";

const props = defineProps({
  selectedDocuments: {
    type: Object as PropType<PMDoc[]>,
    required: true,
  },
  doc: {
    type: Object as PropType<WorkspaceDocumentFragment>,
    required: false,
  },
});

const selection = ref(props.selectedDocuments);

const emit = defineEmits(["close", "goBack", "success"]);

const BASE_URL = import.meta.env.VUE_APP_BACKEND_URL || "/backend";
const store = useStore();

const state = ref<Map<Number, string>>(new Map());

const sendNotification = ref(true);

const importDoc = async (docId: Number) => {
  state.value.set(docId, "running");

  const fetchUrl = BASE_URL + "/veeva/import";
  const headers = new Map();
  headers.set("Authorization", `Bearer ${token.value}`);
  try {
    const response = await HTTP.post(
      fetchUrl,
      headers,
      JSON.stringify({
        veeva_doc_id: docId,
        workspace_id: store.getters.currentWorkspace.id,
        doc_id: props.doc?.id,
        send_notification: sendNotification.value,
      }),
    );

    state.value.set(docId, "success");
  } catch (e) {
    state.value.set(docId, "error");
  }
};

const isRunning = ref(false);

const importDocs = async () => {
  if (selection.value.length === 0) {
    return;
  }

  for (const doc of selection.value) {
    state.value.set(doc.id, "waiting");
  }

  isRunning.value = true;

  let tasks = [];

  for (let i = 0; i < selection.value.length; i++) {
    if (tasks.length === 5) {
      await Promise.all(tasks);
      tasks = [];
    }

    tasks.push(importDoc(selection.value[i].id));
  }

  if (tasks.length > 0) {
    await Promise.all(tasks);
  }

  const allSuccess =
    state.value instanceof Map &&
    state.value.size > 0 &&
    [...state.value.values()].every((v) => v === "success");
  if (allSuccess) {
    emit("success");
    emit("close");
  }
};

const removeFromSelection = (docId: Number) => {
  selection.value = selection.value.filter((doc) => doc.id !== docId);
  if (selection.value.length === 0) {
    emit("goBack", selection.value);
  }
};
</script>

<template>
  <Modal
    :active="true"
    @close="emit('close')"
    class="huge"
    :closeDisabled="true"
    data-test="upload-modal"
  >
    <template v-slot:header>
      {{ $t("promomatsIntegration.title") }}
    </template>
    <template v-slot:body>
      <div class="content">
        <table class="data_table">
          <thead>
            <tr>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th>
                <template v-if="isRunning">
                  {{ $t("promomatsIntegration.columns.status") }}
                </template>
                <template v-else>
                  {{ $t("promomatsIntegration.columns.remove") }}
                </template>
              </th>
            </tr>
          </thead>
          <tbody>
            <Document
              v-for="(doc, idx) in selection"
              :key="idx"
              :doc="doc"
              :isSelecteable="false"
              :selected="true"
            >
              <span v-if="state.get(doc.id) === 'running'">
                <progress-spinner />
              </span>
              <span v-else-if="state.get(doc.id) === 'success'">
                <span class="material-symbols-outlined success"> done </span>
              </span>
              <span v-else-if="state.get(doc.id) === 'error'">
                <span class="material-symbols-outlined error"> error </span>
              </span>
              <div v-else-if="state.get(doc.id) === 'error'" class="error">
                {{ $t(`upload.error.title`) }}:
              </div>
              <span v-else>
                <span
                  class="material-symbols-outlined clickable"
                  @click="removeFromSelection(doc.id)"
                >
                  close
                </span>
              </span>
            </Document>
          </tbody>
        </table>
      </div>
    </template>
    <template #footer>
      <div class="buttons">
        <template v-if="!isRunning">
          <div class="button left" @click="emit('goBack', selection)">
            <span class="material-symbols-outlined clickable">
              arrow_back
            </span>
            {{ $t("promomatsIntegration.back") }}
          </div>
          <div class="checkbox_container right">
            <input type="checkbox" v-model="sendNotification" /><label>{{
              $t("upload.sendNotificationLabel")
            }}</label>
          </div>
          <button class="primary button" @click="importDocs">
            {{
              $t("promomatsIntegration.submit", {
                numDocs: selection.length,
              })
            }}
          </button>
        </template>

        <template v-else>
          <button class="button disabled">
            {{ $t("promomatsIntegration.isRunning.label") }}
          </button>
        </template>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
table.data_table {
  box-shadow: none;
  table-layout: fixed;
}

table tr th:last-child,
table tr td:last-child {
  text-align: center;
  width: 70px;
}

.content {
  height: 100%;
  overflow: scroll;
  box-sizing: border-box;
  padding: 0.5em 1em 0 0;
}

.material-symbols-outlined {
  vertical-align: middle;
}

.material-symbols-outlined.clickable:hover {
  cursor: pointer;
  opacity: 1;
}

.clickable.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.success {
  color: var(--classic-success, #48c78e);
}

.error {
  color: var(--classic-error-text, #9f3a38);
}

.header .material-symbols-outlined {
  margin-right: 0.5em;
}

.checkbox_container {
  display: flex;
  margin-right: 1em;
  align-items: center;
}

.checkbox_container label {
  margin-left: 0.5em;
}

.buttons label {
  font-size: 0.9em;
}
</style>
